import PropTypes from 'prop-types';
import React from 'react';

import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import { Badge, Button, Menu, Tooltip, Copy01, DotsGrid, DotsVertical, Edit01, InfoCircle, PauseCircle, PlayCircle, Trash01 } from '@opendate-app/setlist';

import { salesWindowInverted, INVERTED_WINDOW_TICKET_WARNING, TIMED_ENTRY_UNUSABLE_TICKET_WARNING } from '../../shared/utils/showDetailWarnings';

const axios = require('axios').default;
import { toast } from 'react-toastify';
import TicketTypeInventoryStats from '../../../shared/TicketTypeInventoryStats';
import DemandBadge from '../../shared/components/DemandBadge';
import DynamicPriceDisplay from '../../shared/components/DynamicPriceDisplay';
import { pricePointTriggerLabel } from '../utils/ticketPricePoints';
import { SALES_PAUSED_TITLE, SALES_PAUSED_DETAIL, SALES_PAUSED_DESCRIPTION, salesPauseActionLabel, toggleTicketTypeSalesPaused } from '../utils/ticketTypeSalesPause';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

const ticketTypeCurrentlyHidden = (ticketType) => {
  var now = new Date();
  var startTime = new Date(ticketType.start_time);

  return (
    ticketType.visibility === "Hidden"
      || (
        ticketType.visibility === "Hidden when not on sale"
          && now <=  startTime
      )
  );
}

const ticketTypeStartTime = (ticketType) => {
  if (!ticketType.start_time || !ticketType.end_time) {
    return "Unscheduled";
  }

  var startTimePieces = [];

  if(ticketType.scheduled){
    startTimePieces.push("Scheduled");
  }

  if(ticketType.wait_for_ticket_type && ticketType.wait_for_ticket_type.id){
    startTimePieces.push("Starts after " + ticketType.wait_for_ticket_type.base_name);
  } else {
    startTimePieces.push(ticketType.human_start_time);
  }

  return startTimePieces.join(" • ");
}

const TicketTypeRow = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  fetchNewTicketType,
  ticketType,
  fetchTicketTypeForEdit,
  ticketTypeIsSeated,
  ticketGroup,
  isLast,
  ticketGroupChanged,
  ticketTypesChanged,
  canAddSubGroup
}) => {
  const deleteTicketType = (csrfToken, team, ticketType) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete("/teams/" + team.id + "/ticket_types/" + ticketType.id)
      .then(({ data }) => {
        var updated = Object.assign({}, ticketGroup, {
          ticket_types: ticketGroup.ticket_types.filter((ticketType) => ticketType.id !== data.id)
        });

        ticketGroupChanged(updated);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: ticketType.id,
    data: {
      id: ticketType.id,
      ticketType: ticketType,
      ticketGroup: ticketGroup
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const ticketTypeCapacityLargerThanGroup = (
    ticketType.available > parseFloatFromString(ticketGroup.capacity || 0)
  )

  const overflowActions = [
    {
      label: 'Edit',
      icon: Edit01,
      onClick: () => fetchTicketTypeForEdit(
        csrfToken, team, ticketType.price_parent_id ? { ...ticketType, id: ticketType.price_parent_id } : ticketType, ticketGroup,
      ),
    },
    ...(ticketType.presale_parent_id || ticketType.price_parent_id ? [] : [
      {
        label: salesPauseActionLabel(ticketType),
        icon: ticketType.sales_paused ? PlayCircle : PauseCircle,
        onClick: () => toggleTicketTypeSalesPaused(team, ticketType, ticketTypesChanged),
      },
    ]),
    ...(ticketTypeIsSeated(ticketType) ? [] : [
      {
        label: 'Copy',
        icon: Copy01,
        onClick: () => fetchNewTicketType(csrfToken, team, confirm, ticketGroup, ticketType),
      },
      {
        label: 'Delete',
        icon: Trash01,
        destructive: true,
        onClick: () => deleteTicketType(csrfToken, team, ticketType),
      },
    ]),
  ];

  return (
  <tr ref={setNodeRef} style={style} className={!canAddSubGroup && isLast ? "border-0" : ""}>
    <td
      data-label=""
      className="px-1 text-center d-none d-md-table-cell"
      style={{verticalAlign: "top"}}
    >
      <a
        {...attributes}
        {...listeners}
        href="#"
        className="py-0 btn btn-link btn-sm small"
        style={{color: "var(--sl-text-disabled)"}}
        onClick={(e) => e.preventDefault()}
        aria-label="Reorder ticket"
      >
        <DotsGrid width={16} height={16} aria-hidden="true" />
      </a>
    </td>
    <td data-label="Ticket Name" className="overflow-ellipsis">
      <div>
        <Tooltip title={ticketType.base_name}>
          <strong>
            {ticketType.base_name}
          </strong>
        </Tooltip>
        {ticketType.sales_paused ? (
          <Tooltip title={SALES_PAUSED_TITLE} supportingText={SALES_PAUSED_DETAIL} maxWidth={260}>
            <span className="d-inline-flex ml-2 text-warning" role="img" aria-label={SALES_PAUSED_DESCRIPTION}>
              <PauseCircle width={16} height={16} aria-hidden="true" />
            </span>
          </Tooltip>
        ) : null}
        {ticketType.dynamic_pricing_enabled && (
          <span className="ml-2">
            <Badge color="blue" size="sm">⚡ Dynamic</Badge>
          </span>
        )}
        <DemandBadge enabled={ticketType.dynamic_pricing_enabled} demandStatus={confirm.demand_status} />
      </div>
      {ticketType.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          <span style={{marginRight: "4px"}}>
            <Badge color="brand" size="sm">Price Change {index + 1}</Badge>
          </span>
          {pricePoint.name}
        </div>
      ))}
    </td>
    <td data-label="On Sale Date">
      <div>
        <span style={(!ticketType.start_time || !ticketType.end_time || salesWindowInverted(ticketType)) ? {color: "var(--sl-text-error)"} : null}
              title={ticketTypeStartTime(ticketType)}>
          {ticketTypeStartTime(ticketType)}
          {ticketTypeCurrentlyHidden(ticketType) ? (
            <i className="fas fa-eye-slash small ml-2"></i>
          ) : null}
          {salesWindowInverted(ticketType) ? (
            <i className="fas fa-triangle-exclamation small ml-2"
               role="img"
               aria-label={INVERTED_WINDOW_TICKET_WARNING}
               title={INVERTED_WINDOW_TICKET_WARNING}></i>
          ) : null}
          {ticketType.timed_entry_window_unusable ? (
            <i className="fas fa-clock small ml-2"
               style={{color: "var(--sl-text-error)"}}
               role="img"
               aria-label={TIMED_ENTRY_UNUSABLE_TICKET_WARNING}
               title={TIMED_ENTRY_UNUSABLE_TICKET_WARNING}></i>
          ) : null}
        </span>
      </div>
      {ticketType.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          {pricePointTriggerLabel(pricePoint)}
        </div>
      ))}
    </td>
    <td data-label="Price">
      <div>
        {ticketType.free ? (
          "FREE"
        ) : (
          <DynamicPriceDisplay
            enabled={ticketType.dynamic_pricing_enabled}
            currentPrice={ticketType.dynamic_pricing_current_price}
            basePrice={ticketType.base_price}
            formatter={formatter}
            fallback={formatter.format(ticketType.base_price)}
          />
        )}
      </div>
      {ticketType.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          {formatter.format(pricePoint.price)}
        </div>
      ))}
    </td>
    <td data-label="Inventory">
      <div>
        {ticketTypeCapacityLargerThanGroup ? (
          <span style={{color: "var(--sl-text-error)"}}>
            {ticketType.available}
            <Tooltip title="This ticket's capacity is higher than its group's capacity." placement="top" maxWidth={200}>
              <span className="d-inline-flex ml-2" tabIndex={0} aria-label="About capacity warning" style={{color: "var(--sl-text-tertiary)"}}>
                <InfoCircle width={16} height={16} aria-hidden="true" />
              </span>
            </Tooltip>
          </span>
        ) : (
          <span>
            {ticketType.available}
          </span>
        )}
      </div>
      <TicketTypeInventoryStats item={ticketType} />
    </td>
    <td
      data-label=""
      className="text-center"
      style={{verticalAlign: "top"}}
    >
      <Menu
        placement="bottom-end"
        items={overflowActions}
        trigger={(
          <Button
            variant="tertiary"
            size="sm"
            icon={DotsVertical}
            aria-label={`Actions for ${ticketType.base_name}`}
          />
        )}
      />
    </td>
  </tr>
  );
};

TicketTypeRow.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  fetchNewTicketType: PropTypes.func.isRequired,
  ticketType: PropTypes.object.isRequired,
  fetchTicketTypeForEdit: PropTypes.func.isRequired,
  ticketTypeIsSeated: PropTypes.func.isRequired,
  ticketGroup: PropTypes.object.isRequired,
  isLast: PropTypes.bool.isRequired,
  ticketGroupChanged: PropTypes.func.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  canAddSubGroup: PropTypes.bool.isRequired
};

export default TicketTypeRow;

